import { useEffect, useMemo, useRef } from 'react'

import { useSelector } from 'react-redux'

import type { MenuResponse } from '@library/api-menu-experience'
import type { RecipeUUID } from '@library/menu-service'

import { getBasketDate, getBasketRecipes, getNumPortions } from 'selectors/basket'
import { getBasketRecipeNumPortions, getBasketRecipeQuantity } from 'utils/basket'

import type { MenuQueryParams } from '../../../context/menuQueryContext'
import { getMenuExperienceMenuSWRKey } from './useMenuExperienceNavigation'
import { useMenuExperienceRecipeTileRefresh } from './useMenuExperienceRecipeTileRefresh'

type MenuExperienceBasketRecipeTileSyncQuery = MenuQueryParams &
  Record<string, unknown> & { collection?: string }

export const useMenuExperienceBasketRecipeTileSync = (
  query: MenuExperienceBasketRecipeTileSyncQuery,
  menuId: string,
  menuContent?: MenuResponse['content'],
) => {
  const deliveryDate = useSelector(getBasketDate)
  const basketRecipes = useSelector(getBasketRecipes)
  const basketNumPortions = useSelector(getNumPortions)
  const { refreshRecipeTile } = useMenuExperienceRecipeTileRefresh({ menuId, menuContent, query })
  const menuSWRKey = getMenuExperienceMenuSWRKey({
    deliveryDate,
    numPortions: basketNumPortions,
    query,
  })
  const menuSWRKeyId = JSON.stringify(menuSWRKey)
  const previousSnapshotRef = useRef(new Map<RecipeUUID, number>())
  const previousMenuIdRef = useRef(menuId)
  const previousMenuSWRKeyIdRef = useRef(menuSWRKeyId)

  const snapshot = useMemo(() => {
    const nextSnapshot = new Map<RecipeUUID, number>()

    basketRecipes.forEach((recipe, recipeId) => {
      const quantity = getBasketRecipeQuantity(recipe)

      if (quantity > 0) {
        nextSnapshot.set(recipeId, getBasketRecipeNumPortions(recipe, basketNumPortions))
      }
    })

    return nextSnapshot
  }, [basketNumPortions, basketRecipes])

  useEffect(() => {
    const hasMenuChanged = previousMenuIdRef.current !== menuId
    const hasMenuSWRKeyChanged = previousMenuSWRKeyIdRef.current !== menuSWRKeyId
    const shouldReconcileAll = hasMenuChanged || hasMenuSWRKeyChanged
    const previousSnapshot = previousSnapshotRef.current
    const snapshotToCompare = shouldReconcileAll ? new Map<RecipeUUID, number>() : previousSnapshot

    previousSnapshotRef.current = snapshot
    previousMenuIdRef.current = menuId
    previousMenuSWRKeyIdRef.current = menuSWRKeyId
    snapshot.forEach((numPortions, recipeId) => {
      const previousNumPortions = snapshotToCompare.get(recipeId)
      const hasCustomPortionsBeenAdded =
        previousNumPortions === undefined && numPortions !== basketNumPortions
      const havePortionsChanged =
        previousNumPortions !== undefined && previousNumPortions !== numPortions

      if (hasCustomPortionsBeenAdded || havePortionsChanged) {
        void refreshRecipeTile(recipeId, numPortions)
      }
    })

    previousSnapshot.forEach((_numPortions, recipeId) => {
      if (!snapshot.has(recipeId)) {
        void refreshRecipeTile(recipeId, basketNumPortions)
      }
    })
  }, [basketNumPortions, menuId, menuSWRKeyId, refreshRecipeTile, snapshot])
}
